import { useState, useEffect, useRef, useCallback } from 'react';
import { RoleArt } from '../components/RoleArt.js';
import { PhaseFlash } from '../components/PhaseFlash.js';
import { ExitRoomButton } from '../components/ExitRoomButton.js';
import { usePerfLite } from '../usePerfLite.js';
import { summarizeTeamVotes } from '../teamVotes.js';
import { reconcileSubmits, SUBMIT_GRACE_MS } from '../nightSubmit.js';
import { ringSeats } from '../nightRing.js';
import type { ActionVerb, ClientMsg, Lang, PlayerView } from '../../../shared/src/types.js';
import { renderKnowledge } from '../../../shared/src/knowledge.js';
import { ROLE_BY_ID } from '../../../shared/src/roles/index.js';

interface Props {
  t: Lang;
  view: PlayerView;
  send: (msg: ClientMsg) => void;
}

interface VerbCopy {
  call: string;
  prompt: string;
  submit: string;
}

const VERB_COPY: Record<ActionVerb, Record<Lang, VerbCopy>> = {
  kill: {
    ru: { call: 'УБИЙСТВО', prompt: 'ВЫБЕРИ ЖЕРТВУ', submit: 'УБИТЬ' },
    uk: { call: 'ВБИВСТВО', prompt: 'ОБЕРИ ЖЕРТВУ', submit: 'ВБИТИ' },
  },
  check: {
    ru: { call: 'ПРОВЕРКА', prompt: 'ПРОВЕРЬ ИГРОКА', submit: 'ПРОВЕРИТЬ' },
    uk: { call: 'ПЕРЕВІРКА', prompt: 'ПЕРЕВІР ГРАВЦЯ', submit: 'ПЕРЕВІРИТИ' },
  },
  save: {
    ru: { call: 'ЛЕЧЕНИЕ', prompt: 'СПАСИ ОДНОГО', submit: 'СПАСТИ' },
    uk: { call: 'ЛІКУВАННЯ', prompt: 'ВРЯТУЙ ОДНОГО', submit: 'ВРЯТУВАТИ' },
  },
  distract: {
    ru: { call: 'ВИЗИТ', prompt: 'ОТВЛЕКИ ИГРОКА', submit: 'ОТВЛЕЧЬ' },
    uk: { call: 'ВІЗИТ', prompt: 'ВІДВОЛІКАЙ ГРАВЦЯ', submit: 'ВІДВОЛІКТИ' },
  },
  douse: {
    ru: { call: 'ОБЛИВАНИЕ', prompt: 'ОБЛЕЙ ЖЕРТВУ', submit: 'ОБЛИТЬ' },
    uk: { call: 'ОБЛИВАННЯ', prompt: 'ОБЛИЙ ЖЕРТВУ', submit: 'ОБЛИТИ' },
  },
  ignite: {
    ru: { call: 'ПОДЖОГ', prompt: 'ПОДОЖГИ ОБЛИТЫХ', submit: 'ПОДЖЕЧЬ' },
    uk: { call: 'ПІДПАЛ', prompt: 'ПІДПАЛИ ОБЛИТИХ', submit: 'ПІДПАЛИТИ' },
  },
  shield: {
    ru: { call: 'ЩИТ', prompt: 'ЗАЩИТИ ЦЕЛЬ', submit: 'ЗАЩИТИТЬ' },
    uk: { call: 'ЩИТ', prompt: 'ЗАХИСТИ ЦІЛЬ', submit: 'ЗАХИСТИТИ' },
  },
  protect: {
    ru: { call: 'ОХРАНА', prompt: 'ВСТАНЬ НА ЗАЩИТУ', submit: 'ОХРАНЯТЬ' },
    uk: { call: 'ОХОРОНА', prompt: 'ВСТАНЬ НА ЗАХИСТ', submit: 'ОХОРОНЯТИ' },
  },
  redirect: {
    ru: { call: 'ПОДМЕНА', prompt: 'ВЫБЕРИ ДВУХ', submit: 'ПОМЕНЯТЬ' },
    uk: { call: 'ПІДМІНА', prompt: 'ОБЕРИ ДВОХ', submit: 'ПОМІНЯТИ' },
  },
  alert: {
    ru: { call: 'ДОЗОР', prompt: 'ВСТАТЬ НА ДОЗОР?', submit: 'ВКЛЮЧИТЬ ДОЗОР' },
    uk: { call: 'ВАРТА', prompt: 'СТАТИ НА ВАРТУ?', submit: 'УВІМКНУТИ ВАРТУ' },
  },
  frame: {
    ru: { call: 'ПОДСТАВА', prompt: 'ВЫБЕРИ ЖЕРТВУ', submit: 'ПОДСТАВИТЬ' },
    uk: { call: 'ПІДСТАВА', prompt: 'ОБЕРИ ЖЕРТВУ', submit: 'ПІДСТАВИТИ' },
  },
  vest: {
    ru: { call: 'БРОНЕЖИЛЕТ', prompt: 'НАДЕТЬ ЖИЛЕТ?', submit: 'НАДЕТЬ' },
    uk: { call: 'БРОНЕЖИЛЕТ', prompt: 'ОДЯГНУТИ ЖИЛЕТ?', submit: 'ОДЯГНУТИ' },
  },
  silence: {
    ru: { call: 'МОЛЧАНИЕ', prompt: 'ЗАМОЛЧИ ЦЕЛЬ', submit: 'УСЫПИТЬ' },
    uk: { call: 'МОВЧАННЯ', prompt: 'ЗАМОВКНИ ЦІЛЬ', submit: 'ПРИСИПИТИ' },
  },
  watch: {
    ru: { call: 'СЛЕЖКА', prompt: 'СЛЕДИ ЗА ИГРОКОМ', submit: 'СЛЕДИТЬ' },
    uk: { call: 'СТЕЖЕННЯ', prompt: 'СТЕЖ ЗА ГРАВЦЕМ', submit: 'СТЕЖИТИ' },
  },
  inspect: {
    ru: { call: 'ДОСЬЕ', prompt: 'УЗНАЙ РОЛЬ', submit: 'ДОСЬЕ' },
    uk: { call: 'ДОСЬЄ', prompt: 'ДІЗНАЙСЯ РОЛЬ', submit: 'ДОСЬЄ' },
  },
  execute: {
    ru: { call: 'КАЗНЬ', prompt: 'КАЗНИТЬ ИГРОКА', submit: 'КАЗНИТЬ' },
    uk: { call: 'СТРАТА', prompt: 'СТРАТИТИ ГРАВЦЯ', submit: 'СТРАТИТИ' },
  },
  investigate: {
    ru: { call: 'РАССЛЕДОВАНИЕ', prompt: 'УЗНАЙ ОПАСНОСТЬ', submit: 'ПРОВЕРИТЬ' },
    uk: { call: 'РОЗСЛІДУВАННЯ', prompt: 'ДІЗНАЙСЯ НЕБЕЗПЕКУ', submit: 'ПЕРЕВІРИТИ' },
  },
  reveal: {
    ru: { call: 'ОТКРОВЕНИЕ', prompt: 'ОБЪЯВИ РОЛЬ', submit: 'ОТКРЫТЬ' },
    uk: { call: 'ВІДКРИТТЯ', prompt: 'ОГОЛОСИ РОЛЬ', submit: 'ВІДКРИТИ' },
  },
  spy: {
    ru: { call: 'ПОДСЛУШИВАНИЕ', prompt: 'СЛУШАТЬ МАФИЮ', submit: 'СЛУШАТЬ' },
    uk: { call: 'ПІДСЛУХОВУВАННЯ', prompt: 'СЛУХАТИ МАФІЮ', submit: 'СЛУХАТИ' },
  },
  jail: {
    ru: { call: 'ТЮРЬМА', prompt: 'ЗАБЕРИ В КАМЕРУ', submit: 'ПОСАДИТЬ' },
    uk: { call: 'В’ЯЗНИЦЯ', prompt: 'ЗАБЕРИ ДО КАМЕРИ', submit: 'ПОСАДИТИ' },
  },
  track: {
    ru: { call: 'ПОИСК', prompt: 'ИДИ ПО СЛЕДУ', submit: 'ВЫСЛЕДИТЬ' },
    uk: { call: 'ПОШУК', prompt: 'ЙДИ СЛІДАМИ', submit: 'ВИСЛІДИТИ' },
  },
  taunt: {
    ru: { call: 'ШУТКА', prompt: 'ПОДДРАЗНИТЬ ГОРОД?', submit: 'ПОДДРАЗНИТЬ' },
    uk: { call: 'ЖАРТ', prompt: 'ПОДРАЖНИТИ МІСТО?', submit: 'ПОДРАЖНИТИ' },
  },
  plague: {
    ru: { call: 'ЧУМА', prompt: 'ЗАРАЗИ ИГРОКА', submit: 'ЗАРАЗИТЬ' },
    uk: { call: 'ЧУМА', prompt: 'ЗАРАЗИ ГРАВЦЯ', submit: 'ЗАРАЗИТИ' },
  },
  rampage: {
    ru: { call: 'РЕЗНЯ', prompt: 'ВЫБЕРИ ЖЕРТВУ', submit: 'РАСТЕРЗАТЬ' },
    uk: { call: 'РІЗАНИНА', prompt: 'ОБЕРИ ЖЕРТВУ', submit: 'РОЗТЕРЗАТИ' },
  },
  convert: {
    ru: { call: 'КУЛЬТ', prompt: 'ЗАВЕРБУЙ МИРНОГО', submit: 'ЗАВЕРБОВАТЬ' },
    uk: { call: 'КУЛЬТ', prompt: 'ЗАВЕРБУЙ МИРНОГО', submit: 'ЗАВЕРБУВАТИ' },
  },
  control: {
    ru: { call: 'КОНТРОЛЬ', prompt: 'ВЫБЕРИ МАРИОНЕТКУ И ЦЕЛЬ', submit: 'НАВЕСТИ' },
    uk: { call: 'КОНТРОЛЬ', prompt: 'ОБЕРИ МАРІОНЕТКУ ТА ЦІЛЬ', submit: 'СПРЯМУВАТИ' },
  },
};

/**
 * Summary of where the mafia kill is currently headed. Mirrors the
 * server-side resolver (`summarizeTeamVotes`): the hitman's INDEPENDENT kill
 * never joins the group tally — the resolver queues it separately (audit
 * P2) — so it renders on its own line below the group prediction. Pure
 * presentation — does not mutate or send.
 */
function TeamSummary({
  votes,
  t,
}: {
  votes: NonNullable<PlayerView['mafiaKillVotes']>;
  t: Lang;
}) {
  const s = summarizeTeamVotes(votes);
  const groupText =
    s.group.kind === 'none'
      ? t === 'uk' ? 'Нікого' : 'Никого'
      : s.group.kind === 'target'
        ? t === 'uk' ? `Ціль: ${s.group.targetName}` : `Цель: ${s.group.targetName}`
        : s.group.kind === 'tie-commander'
          ? t === 'uk'
            ? `Нічия — вирішує ${s.group.commanderName}: ${s.group.targetName}`
            : `Ничья — решает ${s.group.commanderName}: ${s.group.targetName}`
          : t === 'uk' ? 'Нічия — ніхто не вбитий' : 'Ничья — никто не убит';
  return (
    <>
      <div className="night-team__summary mono">{groupText}</div>
      {s.independent.map((iv, i) => (
        <div key={i} className="night-team__summary mono">
          {t === 'uk'
            ? `Окремий постріл: ${iv.voterName} → ${iv.targetName}`
            : `Отдельный выстрел: ${iv.voterName} → ${iv.targetName}`}
        </div>
      ))}
    </>
  );
}

/**
 * Live "things are happening" indicator for the dead-end waiting screens
 * (skipped / asleep / dead). Shows the aggregate ready-count from the server
 * plus a breathing dot. The dot pulse is gated on perf-lite so it stays static
 * (but still visible) when decorative motion is suppressed.
 */
function NightWaiting({
  ready,
  t,
}: {
  ready: PlayerView['nightReady'];
  t: Lang;
}) {
  const lite = usePerfLite();
  return (
    <div
      className="night-waiting mono"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 12,
        color: 'var(--bone-dim)',
        letterSpacing: '.16em',
        fontSize: 11,
        position: 'relative',
        zIndex: 5,
      }}
    >
      <span
        className="night-waiting__dot"
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--bone-dim)',
          // Reuse the existing global `flicker` keyframe for the breathing
          // pulse; held static (but visible) under perf-lite.
          animation: lite ? 'none' : 'flicker 2.6s ease-in-out infinite',
        }}
      />
      {/* The server projects only a boolean (audit P2) — exact done/total
          counts let players infer whether a janitor-cleaned victim had a
          night ability, so no count is available to render here. */}
      {ready?.allDone
        ? t === 'ru'
          ? 'все закончили'
          : 'усі закінчили'
        : t === 'ru'
          ? 'ждём остальных…'
          : 'чекаємо на інших…'}
    </div>
  );
}

/**
 * Success echo for a committed night action: a checkmark + the confirmed
 * target name, tinted with the role accent so the locked state reads as "done"
 * rather than a dead disabled button.
 */
function SubmittedEcho({
  accent,
  targetName,
  t,
}: {
  accent: string;
  targetName: string | null;
  t: Lang;
}) {
  return (
    <span className="night-echo" style={{ color: accent }}>
      <span aria-hidden="true">✓</span>{' '}
      {targetName
        ? targetName
        : t === 'ru' ? 'ОТПРАВЛЕНО' : 'НАДІСЛАНО'}
    </span>
  );
}

export function Night({ t, view, send }: Props) {
  const [target, setTarget] = useState<string | null>(null);
  const [target2, setTarget2] = useState<string | null>(null);
  const [submittedVerbs, setSubmittedVerbs] = useState<Set<ActionVerb>>(new Set());
  // Set of verbs the player believed submitted but the server then dropped —
  // drives the transient "НЕ ПРИНЯТО" notice near the action.
  const [rejectedVerbs, setRejectedVerbs] = useState<Set<ActionVerb>>(new Set());
  // "I'm done for tonight" flag. Local state for instant feedback; skip AND
  // its undo are both mirrored to the server (player:skip-night /
  // player:unskip-night) so the host's readiness gate always agrees with what
  // this screen shows. The server echoes the flag back as `me.nightSkipped`
  // (restore effect below) so a remount/reconnect mid-night lands back on the
  // skipped screen instead of re-offering the action.
  const [skipped, setSkipped] = useState(false);

  const prevSubmittedRef = useRef<Set<ActionVerb>>(new Set());
  // In-flight optimistic submits: verb -> Date.now() at send. A broadcast
  // projected BEFORE the server processed the submit (someone else acting,
  // chat) arrives without the verb — absence alone must not flash a false
  // «НЕ ПРИНЯТО» (audit P2). See nightSubmit.ts for the reconcile rules.
  const inflightRef = useRef<Map<ActionVerb, number>>(new Map());
  // Latest server truth, kept so the grace-expiry re-reconcile between
  // broadcasts diffs against real data instead of an empty set.
  const serverVerbsRef = useRef<ActionVerb[]>([]);

  // Reset selection on a new night.
  useEffect(() => {
    setTarget(null);
    setTarget2(null);
    setSubmittedVerbs(new Set());
    setRejectedVerbs(new Set());
    // Clear the diff baseline too, so the emptied myPendingVerbs of a fresh
    // night isn't misread as a rejection of last night's actions.
    prevSubmittedRef.current = new Set();
    inflightRef.current = new Map();
    serverVerbsRef.current = [];
    setSkipped(false);
  }, [view.room.night]);

  // Restore the server-confirmed skip after a remount/reconnect (declared
  // AFTER the new-night reset above so the mount-time ordering is
  // reset-then-restore). One-directional on purpose: the server flag only
  // RAISES the local state — lowering is driven by the player's own unskip
  // tap (which clears both sides), so a stale broadcast captured before the
  // server processed the unskip can never flip the screen back.
  useEffect(() => {
    if (view.me.nightSkipped) setSkipped(true);
  }, [view.me.nightSkipped]);

  // Reconcile the optimistic "submitted" locks against the server's record of
  // what it actually accepted (reconcileSubmits): server truth plus open
  // in-flight grace windows stay locked; a verb covered by neither was
  // silently rejected (target became invalid, charge spent, etc.) and flashes
  // a transient notice instead of leaving the button stuck.
  const applyReconcile = useCallback(() => {
    const res = reconcileSubmits(
      serverVerbsRef.current,
      inflightRef.current,
      prevSubmittedRef.current,
      Date.now(),
    );
    inflightRef.current = res.inflight;
    prevSubmittedRef.current = res.locked;
    setSubmittedVerbs(res.locked);
    if (res.rejected.length) {
      setRejectedVerbs((prev) => {
        const next = new Set(prev);
        for (const v of res.rejected) next.add(v);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    if (!view.myPendingVerbs) return;
    serverVerbsRef.current = view.myPendingVerbs;
    applyReconcile();
  }, [view.myPendingVerbs, applyReconcile]);

  // While any submit is unacked, re-reconcile the moment the oldest grace
  // window closes — that's when an unacked submit becomes a real rejection
  // even if no further broadcast arrives. Re-arms after every reconcile via
  // the submittedVerbs identity change.
  useEffect(() => {
    if (inflightRef.current.size === 0) return;
    const earliest = Math.min(...inflightRef.current.values());
    const delay = Math.max(0, earliest + SUBMIT_GRACE_MS - Date.now()) + 20;
    const id = window.setTimeout(applyReconcile, delay);
    return () => window.clearTimeout(id);
  }, [submittedVerbs, applyReconcile]);

  // Auto-clear the transient "rejected" notice a few seconds after it appears
  // so it reads as a flash, not a permanent error.
  useEffect(() => {
    if (rejectedVerbs.size === 0) return;
    const id = window.setTimeout(() => setRejectedVerbs(new Set()), 4000);
    return () => window.clearTimeout(id);
  }, [rejectedVerbs]);

  const prompts = view.prompts ?? (view.prompt ? [view.prompt] : []);
  const prompt = prompts[0] ?? null;
  const me = view.me;
  // Defensive — only routed here during night phase when role is assigned.
  if (!me.role) return null;
  // Stable narrowed accent — TS loses the `me.role` narrowing inside the
  // ring/action `.map()` closures, so capture it once here.
  const roleAccent = me.role.accent;
  const allies = new Set(
    me.marks.filter((m) => m.startsWith('ally:')).map((m) => m.slice(5)),
  );

  if (skipped) {
    return (
      <div className="scr scr-night">
        <div className="bg-night" />
        <div className="moon" />
        <div className="vig vignette-pulse" />

        <PhaseFlash t={t} kind="night-fall" signature={`night-${view.room.night}`} />

        <div className="night-head">
          <div className="phase-pill phase-night">
            <span className="moon-dot" />
            <span className="sub">{t === 'ru' ? 'НОЧЬ · ' : 'НІЧ · '}{String(view.room.night).padStart(2, '0')}</span>
          </div>
          <div className="display night-call" style={{ color: 'var(--bone-dim)' }}>
            {t === 'ru' ? 'ВЫ ПРОПУСТИЛИ' : 'ВИ ПРОПУСТИЛИ'}
          </div>
          <div className="mono night-prompt">
            {t === 'ru' ? 'ЖДЁМ ДЕНЬ' : 'ЧЕКАЄМО ДЕНЬ'}
          </div>
        </div>

        <NightWaiting ready={view.nightReady} t={t} />

        {/* Undo must reach the server too (audit P2): a local-only
            setSkipped(false) left the server's nightSkipped flag set, so the
            host gate kept counting this player as done. */}
        <div className="night-skip-row">
          <button
            className="btn btn-ghost night-skip-btn"
            onClick={() => {
              send({ kind: 'player:unskip-night' });
              setSkipped(false);
            }}
          >
            <span className="display">
              {t === 'ru' ? 'ОТМЕНИТЬ ПРОПУСК' : 'СКАСУВАТИ ПРОПУСК'}
            </span>
          </button>
        </div>

        <ExitRoomButton t={t} send={send} midGame />
      </div>
    );
  }

  if (!prompt || !me.alive) {
    return (
      <div className="scr scr-night">
        <div className="bg-night" />
        <div className="moon" />
        <div className="vig vignette-pulse" />

        <PhaseFlash t={t} kind="night-fall" signature={`night-${view.room.night}`} />

        <div className="night-head">
          <div className="phase-pill phase-night">
            <span className="moon-dot" />
            <span className="sub">{t === 'ru' ? 'НОЧЬ · ' : 'НІЧ · '}{String(view.room.night).padStart(2, '0')}</span>
          </div>
          <div className="display night-call" style={{ color: 'var(--bone-dim)' }}>
            {me.alive
              ? t === 'ru' ? 'ВЫ СПИТЕ' : 'ВИ СПИТЕ'
              : t === 'ru' ? 'ВЫ МЕРТВЫ' : 'ВИ МЕРТВІ'}
          </div>
          <div className="mono night-prompt">{t === 'ru' ? 'ЖДЁМ КОНЦА НОЧИ' : 'ЧЕКАЄМО КІНЦЯ НОЧІ'}</div>
        </div>

        <NightWaiting ready={view.nightReady} t={t} />

        <ExitRoomButton t={t} send={send} midGame />
      </div>
    );
  }

  const ability = prompt.ability;
  const copy = VERB_COPY[ability.key][t];
  const isArsonist = me.role.id === 'arsonist';
  const isTwoTarget = ability.shape === 'two-target';
  const isTargetless = ability.targetless || ability.shape === 'targetless';
  // Multi-prompt roles (currently the Don: kill + check) get every eligible
  // ability rendered; arsonist keeps its bespoke douse/ignite bench below.
  const showSecondaryPrompts = !isArsonist && prompts.length > 1;

  const isSubmitted = (v: ActionVerb) => submittedVerbs.has(v);
  const allPrimarySubmitted = isSubmitted(ability.key);
  // Single-target actions can be re-opened/retargeted after submit: recordAction
  // overwrites server-side for the same verb, so a re-submit is idempotent.
  // Two-target, multi-prompt and arsonist keep the firmer lock (their pickers
  // are stateful enough that re-editing in place is error-prone).
  const isSimpleSingleTarget =
    !showSecondaryPrompts && !isArsonist && !isTwoTarget && !isTargetless;
  const ringLocked =
    isSimpleSingleTarget
      ? false
      : showSecondaryPrompts
        ? prompts.every((p) => isSubmitted(p.ability.key))
        : allPrimarySubmitted;

  // Server-confirmed echo of *this* player's committed targets (survives
  // reconnect). Used to show the chosen target name next to the submitted
  // action, styled as success rather than a dead "ОТПРАВЛЕНО".
  const pendingByVerb = new Map(
    (view.myPendingActions ?? []).map((a) => [a.verb, a]),
  );
  const nameById = new Map(view.room.players.map((p) => [p.id, p.name]));
  const echoTargetName = (verb: ActionVerb): string | null => {
    const a = pendingByVerb.get(verb);
    if (!a) return null;
    const n1 = a.targetId ? nameById.get(a.targetId) : null;
    const n2 = a.targetId2 ? nameById.get(a.targetId2) : null;
    if (n1 && n2) return `${n1} → ${n2}`;
    return n1 ?? null;
  };

  // Step-by-step guidance for two-target abilities. `control` has two distinct
  // slots (puppet vs destination); `redirect` is just first/second. Driven by
  // the current selection so the player always knows what the next tap picks.
  const twoTargetStep =
    isTwoTarget && !ringLocked
      ? !target
        ? ability.key === 'control'
          ? t === 'ru' ? 'ШАГ 1: МАРИОНЕТКА' : 'КРОК 1: МАРІОНЕТКА'
          : t === 'ru' ? 'ШАГ 1: ПЕРВЫЙ' : 'КРОК 1: ПЕРШИЙ'
        : !target2
          ? ability.key === 'control'
            ? t === 'ru' ? 'ШАГ 2: ЦЕЛЬ' : 'КРОК 2: ЦІЛЬ'
            : t === 'ru' ? 'ШАГ 2: ВТОРОЙ' : 'КРОК 2: ДРУГИЙ'
          : null
      : null;

  // Skip-night affordance: only when *every* eligible prompt is optional
  // (so the don's mandatory kill never offers it) and the player hasn't
  // submitted anything this night yet. Pressing it locks the screen and
  // tells the host this player is done.
  const canOfferSkip =
    !skipped &&
    submittedVerbs.size === 0 &&
    prompts.length > 0 &&
    prompts.every((p) => p.canSkip);
  const onSkip = () => {
    send({ kind: 'player:skip-night' });
    setSkipped(true);
  };

  const submit = (verb: ActionVerb, tgt?: string, tgt2?: string) => {
    send({ kind: 'player:action', verb, targetId: tgt, targetId2: tgt2 });
    // Open the ack grace window: until it expires, broadcasts that don't yet
    // carry this verb are treated as interleaved (pre-submit) snapshots, not
    // rejections (audit P2).
    inflightRef.current = new Map(inflightRef.current).set(verb, Date.now());
    // Keep the reconcile diff honest: record the optimistic submit so a later
    // server view that drops this verb is recognised as a rejection.
    const optimistic = new Set(submittedVerbs);
    optimistic.add(verb);
    prevSubmittedRef.current = optimistic;
    setSubmittedVerbs(optimistic);
    // A fresh (re-)submit clears any stale "not accepted" flash for this verb.
    setRejectedVerbs((prev) => {
      if (!prev.has(verb)) return prev;
      const next = new Set(prev);
      next.delete(verb);
      return next;
    });
    // After submitting one ability of a multi-ability role, clear the target
    // so the player can pick a fresh one for the remaining action.
    if (showSecondaryPrompts) {
      setTarget(null);
      setTarget2(null);
    }
  };

  const onTargetTap = (id: string) => {
    if (ringLocked) return;
    if (!isTwoTarget) {
      // Re-tapping the current pick clears it, so a player can change their
      // mind without having to find a different target first.
      setTarget((prev) => (prev === id ? null : id));
      return;
    }
    // Two-target picker — first tap = target, second = target2 (must differ).
    if (id === target2) {
      setTarget2(null);
      return;
    }
    if (!target) setTarget(id);
    else if (id === target) setTarget(null);
    else setTarget2(id);
  };

  // Live mafia kill tally — server populates `view.mafiaKillVotes` only for
  // alive mafia during the night phase, so the block self-hides everywhere
  // else by virtue of the field being undefined.
  const mafiaVotes = view.mafiaKillVotes;
  const teamBlock = mafiaVotes && mafiaVotes.length ? (
    <div className="night-team">
      <div className="night-team__title mono">КОМАНДА</div>
      <ul className="night-team__list">
        {mafiaVotes.map((v) => {
          const isMe = v.voterId === view.me.id;
          const targetLabel = v.targetName
            ? v.targetName
            : t === 'uk' ? '— обирає...' : '— выбирает...';
          return (
            <li
              key={v.voterId}
              className={`night-team__row${isMe ? ' is-me' : ''}`}
            >
              <span className="night-team__name">{v.voterName}</span>
              <span className="night-team__arrow">→</span>
              <span className="night-team__target">{targetLabel}</span>
              {v.isCommander && (
                <span className="night-team__badge">
                  {v.isDon
                    ? (t === 'uk' ? 'ВИРІШАЛЬНИЙ' : 'РЕШАЮЩИЙ')
                    : (t === 'uk' ? 'В. О. ДОНА' : 'И. О. ДОНА')}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <TeamSummary votes={mafiaVotes} t={t} />
    </div>
  ) : null;

  return (
    <div className="scr scr-night">
      <div className="bg-night" />
      <div className="moon" />
      <div className="vig vignette-pulse" />

      <PhaseFlash t={t} kind="night-fall" signature={`night-${view.room.night}`} />

      <div className="night-head">
        <div className="phase-pill phase-night">
          <span className="moon-dot" />
          <span className="sub">
            {t === 'ru' ? 'НОЧЬ · ' : 'НІЧ · '}
            {String(view.room.night).padStart(2, '0')}
          </span>
        </div>
        <div className="display night-call" style={{ color: me.role.accent }}>
          {copy.call}
        </div>
        <div className="mono night-prompt">{copy.prompt}</div>
        {prompt.chargesLeft != null && (
          prompt.chargesLeft === 1 ? (
            // Last charge for a one-shot ability — the next submit is final, so
            // warn loudly in blood-red instead of the calm accent counter.
            // (`--blood` is the audit's intended token; fall back to the
            // existing --poison-red so it's correct today either way.)
            <div
              className="mono night-last-charge"
              style={{ marginTop: 4, fontSize: 10, color: 'var(--blood, var(--poison-red))', fontWeight: 700, letterSpacing: '.18em' }}
            >
              {t === 'ru' ? 'ПОСЛЕДНИЙ ЗАРЯД' : 'ОСТАННІЙ ЗАРЯД'}
            </div>
          ) : (
            <div className="mono" style={{ marginTop: 4, fontSize: 10, color: me.role.accent, letterSpacing: '.18em' }}>
              {t === 'ru' ? 'ЗАРЯДЫ: ' : 'ЗАРЯДИ: '}
              {prompt.chargesLeft}
            </div>
          )
        )}
        {twoTargetStep && (
          <div
            className="mono night-step"
            style={{ marginTop: 4, fontSize: 10, color: me.role.accent, letterSpacing: '.18em' }}
          >
            {twoTargetStep}
          </div>
        )}
      </div>

      {teamBlock}

      {isArsonist && (
        <div className="canister">
          <div style={{ width: 50, height: 60 }}>
            <RoleArt id="arsonist" accent="#ff7a1a" />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* 'БЕНЗИН' reads the same in ru and uk. The old decorative 62%
                meter implied a fuel gauge that tracked nothing, so it's dropped;
                the canister now just labels the arsonist's douse/ignite bench. */}
            <div className="sub" style={{ color: '#ff7a1a' }}>БЕНЗИН</div>
          </div>
        </div>
      )}

      {/* Player ring — hidden for targetless abilities. The ring is also
          shown for multi-prompt roles (Don) even when the *primary* prompt is
          something else, since every prompt for that role currently picks a
          target. */}
      {(!isTargetless || showSecondaryPrompts) && (
        <div className="player-ring">
          {ringSeats(view.room.players, me.id, !!ability.canSelfTarget)
            .map(({ player: p, seat }) => {
              const sel = target === p.id;
              const sel2 = target2 === p.id;
              const ally = allies.has(p.id);
              const ringTargetable = showSecondaryPrompts
                ? prompts.some(
                    (pr) =>
                      !isSubmitted(pr.ability.key) &&
                      pr.targets.includes(p.id),
                  )
                : prompt.targets.includes(p.id);
              // Distinguish *why* a seat is unselectable so it reads as
              // intentional, not broken: dead, locked (action committed, ring
              // frozen) or simply not a valid target this turn.
              const disabled = !ringTargetable || ringLocked;
              const reason: 'dead' | 'locked' | 'ineligible' | null = !p.alive
                ? 'dead'
                : ringLocked && !sel && !sel2
                  ? 'locked'
                  : !ringTargetable
                    ? 'ineligible'
                    : null;
              const reasonText =
                reason === 'dead'
                  ? t === 'ru' ? 'мёртв' : 'мертвий'
                  : reason === 'locked'
                    ? t === 'ru' ? 'ход отправлен' : 'хід надіслано'
                    : reason === 'ineligible'
                      ? t === 'ru' ? 'недоступен' : 'недоступний'
                      : null;
              const ariaLabel = reasonText
                ? `${p.name} — ${reasonText}`
                : t === 'ru' ? `Выбрать ${p.name}` : `Обрати ${p.name}`;
              return (
                <button
                  key={p.id}
                  className={`pring-btn ${!p.alive ? 'dead' : ''} ${reason === 'locked' ? 'locked' : ''} ${reason === 'ineligible' ? 'ineligible' : ''} ${sel ? 'selected' : ''} ${sel2 ? 'selected-2' : ''} ${ally ? 'ally' : ''}`}
                  disabled={disabled}
                  aria-disabled={disabled}
                  aria-label={ariaLabel}
                  onClick={() => onTargetTap(p.id)}
                >
                  {/* Seat number from the FULL roster (audit P2): filtering
                      self out used to renumber everyone after the viewer,
                      disagreeing with the lobby/host numbering. */}
                  <div className="pring-num mono">{String(seat).padStart(2, '0')}</div>
                  <div className="pring-name display">{p.name}</div>
                  {sel && <div className="doused-mark">①</div>}
                  {sel2 && <div className="doused-mark">②</div>}
                  {!p.alive && <div className="dead-mark">✕</div>}
                  {reason === 'ineligible' && (
                    <div
                      className="ineligible-mark"
                      aria-hidden="true"
                      style={{ position: 'absolute', top: 4, left: 6, fontSize: 13, lineHeight: 1, color: 'var(--smoke)' }}
                    >
                      –
                    </div>
                  )}
                </button>
              );
            })}
        </div>
      )}

      {isTargetless && !showSecondaryPrompts && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 5 }}>
          <RoleArt id={me.role.id} accent={me.role.accent} sub={me.role.sub} />
        </div>
      )}

      <div className={`night-actions ${(isArsonist || showSecondaryPrompts) ? '' : 'single'}`}>
        {isArsonist ? (
          <>
            <button
              className="btn btn-orange"
              disabled={!target || isSubmitted('douse')}
              onClick={() => target && submit('douse', target)}
            >
              <span className="display">{VERB_COPY.douse[t].submit}</span>
            </button>
            <button
              className="btn btn-blood"
              disabled={isSubmitted('ignite')}
              onClick={() => submit('ignite')}
            >
              <span className="display">{VERB_COPY.ignite[t].submit}</span>
            </button>
          </>
        ) : showSecondaryPrompts ? (
          // Multi-prompt role (Don): one button per ability sharing the ring.
          // Each picks the currently selected target and locks itself; the
          // others stay actionable so the player can pick a fresh target.
          <>
            {prompts.map((pr) => {
              const verb = pr.ability.key;
              const c = VERB_COPY[verb][t];
              const done = isSubmitted(verb);
              const promptTargetless = pr.ability.targetless || pr.ability.shape === 'targetless';
              const needsTarget = !promptTargetless;
              return (
                <button
                  key={verb}
                  className="btn btn-blood"
                  disabled={done || (needsTarget && !target)}
                  onClick={() =>
                    submit(verb, needsTarget ? target ?? undefined : undefined)
                  }
                >
                  <span className="display">
                    {done ? (
                      <SubmittedEcho accent={roleAccent} targetName={echoTargetName(verb)} t={t} />
                    ) : (
                      c.submit
                    )}
                  </span>
                </button>
              );
            })}
          </>
        ) : isTwoTarget ? (
          <button
            className="btn btn-blood"
            disabled={!target || !target2 || allPrimarySubmitted}
            onClick={() => target && target2 && submit(ability.key, target, target2)}
          >
            <span className="display">
              {allPrimarySubmitted ? (
                <SubmittedEcho accent={roleAccent} targetName={echoTargetName(ability.key)} t={t} />
              ) : target && !target2 ? (
                // Don't show a dead disabled button — tell the player what's
                // missing (pick the second seat).
                t === 'ru' ? 'ВЫБЕРИ ВТОРОГО' : 'ОБЕРИ ДРУГОГО'
              ) : (
                copy.submit
              )}
            </span>
          </button>
        ) : isTargetless ? (
          <button
            className="btn btn-blood"
            disabled={allPrimarySubmitted}
            onClick={() => submit(ability.key)}
          >
            <span className="display">
              {allPrimarySubmitted ? (
                <SubmittedEcho accent={roleAccent} targetName={echoTargetName(ability.key)} t={t} />
              ) : (
                copy.submit
              )}
            </span>
          </button>
        ) : (
          // Single-target: stays tappable after submit so the player can
          // retarget and re-send (recordAction overwrites server-side). The
          // label shows a success echo while the current pick matches the
          // commit (or before the server echo arrives), and invites a re-send
          // once the player picks a different seat.
          (() => {
            const committed = pendingByVerb.get(ability.key)?.targetId;
            // Treat the optimistic interim (no server echo yet) as confirmed so
            // the button reads as success the instant it's pressed.
            const showSuccess =
              allPrimarySubmitted && (committed === undefined || committed === target);
            const retargeted = allPrimarySubmitted && committed !== undefined && committed !== target;
            return (
              <button
                className="btn btn-blood"
                disabled={!target}
                onClick={() => target && submit(ability.key, target)}
              >
                <span className="display">
                  {showSuccess ? (
                    <SubmittedEcho
                      accent={roleAccent}
                      targetName={echoTargetName(ability.key) ?? (target ? nameById.get(target) ?? null : null)}
                      t={t}
                    />
                  ) : retargeted ? (
                    t === 'ru' ? 'ПОМЕНЯТЬ ЦЕЛЬ' : 'ЗМІНИТИ ЦІЛЬ'
                  ) : (
                    copy.submit
                  )}
                </span>
              </button>
            );
          })()
        )}
      </div>

      {/* Transient "your action was silently rejected" notice. recordAction
          returns silently on the server, so the only signal is the verb
          vanishing from myPendingVerbs — this turns that into visible feedback. */}
      {[...rejectedVerbs].some((v) => prompts.some((p) => p.ability.key === v)) && (
        <div
          className="night-rejected mono"
          role="alert"
          style={{ color: 'var(--blood, var(--poison-red))', textAlign: 'center', marginTop: 8, fontSize: 11, letterSpacing: '.16em', position: 'relative', zIndex: 5 }}
        >
          {t === 'ru' ? 'НЕ ПРИНЯТО — ПОПРОБУЙ СНОВА' : 'НЕ ПРИЙНЯТО — СПРОБУЙ ЗНОВУ'}
        </div>
      )}

      {canOfferSkip && (
        // Stays out of the action grid so it never competes with the primary
        // verb for thumb taps. Hidden once the player commits an action so
        // we don't muddy the "did I actually skip?" question.
        <div className="night-skip-row">
          <button className="btn btn-ghost night-skip-btn" onClick={onSkip}>
            <span className="display">
              {t === 'ru' ? 'ПРОПУСТИТЬ ХОД' : 'ПРОПУСТИТИ ХІД'}
            </span>
          </button>
        </div>
      )}

      {/* Persistent knowledge log — sheriff/medium/etc see their history. */}
      {me.knowledge.length > 0 && (
        <div className="knowledge-log">
          {me.knowledge.slice(-3).map((k, i) => (
            <div key={i} className="mono knowledge-line">
              <span style={{ color: 'var(--smoke)' }}>N{k.night}·</span>{' '}
              {renderKnowledge(k.event, t, ROLE_BY_ID)}
            </div>
          ))}
        </div>
      )}

      <ExitRoomButton t={t} send={send} midGame />
    </div>
  );
}
